"use client";

import axiosInstance, { isAxiosError } from "@/utils/axiosInstance";
import { toast } from "react-toastify";

interface SettlementPayload {
  start_date: string;
  end_date: string;
  employee_id?: number | string | null;
  payment_method_id?: number | string | null;
  paid_at?: string | null;
}

export const settleNormalCommission = async (
  data: SettlementPayload,
  options?: {
    autoCreate?: boolean;
    establishmentSlug?: string;
    establishmentId?: number | string | null;
  },
) => {
  try {
    const slug = options?.establishmentSlug;
    const url = slug ? `/commissions/settlement` : `/commissions/settlement`;

    const params: Record<string, any> = { auto_create: true };
    if (options?.establishmentId)
      params.filter = { establishment_id: options.establishmentId };

    const body = {
      ...data,
      ...(options?.autoCreate ? { auto_create: true } : {}),
    };

    const response = await axiosInstance.post(url, body, { params });
    toast.success("Comissões de serviços baixadas com sucesso.");
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const backend = error.response?.data as any;

      // If backend returns 409, show friendly message (no pending values)
      if (error.response?.status === 409) {
        const message =
          backend?.message ||
          "Não há valores pendentes para o período selecionado.";
        toast.error(message);
        throw backend || error;
      }

      const message =
        backend && typeof backend === "object" && "message" in backend
          ? backend.message
          : error.message;
      toast.error(message || "Erro ao dar baixa nas comissões.");
      throw backend || error;
    }

    if (error instanceof Error) {
      toast.error(error.message);
      throw error;
    }

    toast.error("Erro desconhecido ao dar baixa nas comissões.");
    throw new Error("unknown_error");
  }
};

export const settleSubscriptionCommission = async (
  data: SettlementPayload,
  options?: {
    autoCreate?: boolean;
    establishmentSlug?: string;
    establishmentId?: number | string | null;
  },
) => {
  try {
    const slug = options?.establishmentSlug;
    const url = slug
      ? `/subscriptions/settlement`
      : `/subscriptions/settlement`;

    const params: Record<string, any> = {};
    if (options?.establishmentId)
      params.filter = { establishment_id: options.establishmentId };

    const body = {
      ...data,
      ...(options?.autoCreate ? { auto_create: true } : {}),
    };

    const response = await axiosInstance.post(url, body, { params });
    const respData = response?.data as any;

    // if backend explicitly reports no pending amount, show its message as info
    if (respData && respData.data && respData.data.pending_amount === 0) {
      const msg =
        respData.message ||
        "Nenhuma comissão de assinatura pendente para esse período.";
      toast.info(msg);
    } else {
      toast.success("Comissões de assinatura baixadas com sucesso.");
    }

    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const backend = error.response?.data as any;

      // If backend returns 409, show friendly message (no pending values)
      if (error.response?.status === 409) {
        const message =
          backend?.message ||
          "Não há valores pendentes para o período selecionado.";
        toast.error(message);
        throw backend || error;
      }

      const message =
        backend && typeof backend === "object" && "message" in backend
          ? backend.message
          : error.message;
      toast.error(message || "Erro ao dar baixa nas comissões.");
      throw backend || error;
    }

    if (error instanceof Error) {
      toast.error(error.message);
      throw error;
    }

    toast.error("Erro desconhecido ao dar baixa nas comissões.");
    throw new Error("unknown_error");
  }
};

export default function useSettleCommissions() {
  return { settleNormalCommission, settleSubscriptionCommission };
}
